import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ReviewActivityLogEvent,
  listReviewTaskActivityLog,
} from '../../../api/reviewTasks'
import { LoadingIndicator } from '../../common/LoadingIndicator'

import {
  formatActivityGap,
  formatDurationHoursMinutes,
  REVIEW_ACTIVITY_PAUSE_MS,
} from './reviewTaskFormat'
import {
  ActionButton,
  CancelButton,
  ConfirmOverlay,
  LogModalCard,
  LogModalHeader,
  LogModalActions,
  LogTableScroll,
  LogTable,
  LogTh,
  LogTd,
  LogPauseTd,
  EmptyState,
} from './styles'

const EVENT_LABELS: Record<string, string> = {
  item_marked_installed: 'Marked installed',
  item_marked_uninstalled: 'Marked uninstalled',
  ui_stl_overlay_toggled: '3D overlay toggled',
  ui_previous_image_overlay_toggled: 'Previous image overlay toggled',
  ai_suggestion_not_applied: 'AI suggestion not applied',
  point_navigated: 'Moved forward',
}

const csvEscape = (value: unknown) => {
  const text = value == null ? '' : String(value)
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

const formatEventLabel = (
  eventType: string,
  metadata: ReviewActivityLogEvent['metadata'],
) => {
  if (eventType === 'point_navigated') {
    const source = metadata && metadata.source
    if (source === 'minimap') {
      return 'Jumped from map'
    }
    if (source === 'address_bar') {
      return 'Opened from address bar'
    }
    if (source === 'auto') {
      return 'Jumped to nearest suggestion'
    }
    if (metadata && metadata.direction === 'back') {
      return 'Moved back'
    }
    return 'Moved forward'
  }
  return EVENT_LABELS[eventType] || eventType
}

type ActivityDisplayRow =
  | {
      kind: 'event'
      event: ReviewActivityLogEvent
      duration: string | null
    }
  | {
      kind: 'pause'
      key: string
      duration: string
    }

const buildActivityDisplayRows = (
  events: ReviewActivityLogEvent[],
): ActivityDisplayRow[] => {
  const rows: ActivityDisplayRow[] = []
  for (let index = 0; index < events.length; index += 1) {
    const newer = events[index]
    const older = events[index + 1]
    let duration: string | null = null
    let pause: ActivityDisplayRow | null = null
    if (newer.registered_on && older?.registered_on) {
      const gap =
        new Date(newer.registered_on).getTime() -
        new Date(older.registered_on).getTime()
      if (gap > REVIEW_ACTIVITY_PAUSE_MS) {
        pause = {
          kind: 'pause',
          key: `pause-${newer.id}-${older.id}`,
          duration: formatActivityGap(gap),
        }
      } else if (gap >= 0) {
        duration = formatActivityGap(gap)
      }
    }
    rows.push({ kind: 'event', event: newer, duration })
    if (pause) {
      rows.push(pause)
    }
  }
  return rows
}

const formatMetadata = (metadata: ReviewActivityLogEvent['metadata']) => {
  if (!metadata || typeof metadata !== 'object') {
    return ''
  }
  const enabled = metadata.enabled
  if (typeof enabled === 'boolean') {
    return enabled ? 'on' : 'off'
  }
  try {
    return JSON.stringify(metadata)
  } catch {
    return ''
  }
}

interface ReviewActivityLogModalProps {
  projectId: string
  taskId: number
  onClose: () => void
}

export const ReviewActivityLogModal = ({
  projectId,
  taskId,
  onClose,
}: ReviewActivityLogModalProps) => {
  const [events, setEvents] = useState<ReviewActivityLogEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nextOffset, setNextOffset] = useState<number | null>(null)
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const [reviewActiveSeconds, setReviewActiveSeconds] = useState<number | null>(
    null,
  )

  const loadPage = useCallback(
    async (offset: number, append: boolean) => {
      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
        setError(null)
      }
      try {
        const response = await listReviewTaskActivityLog(projectId, taskId, {
          limit: 100,
          offset,
        })
        const page = response.data || []
        setEvents(current => (append ? [...current, ...page] : page))
        const count = response.meta?.count
        setTotalCount(typeof count === 'number' ? count : null)
        const reviewSeconds = response.meta?.review_active_seconds
        if (typeof reviewSeconds === 'number') {
          setReviewActiveSeconds(reviewSeconds)
        }
        const suggested = response.meta?.next_offset
        if (
          typeof suggested === 'number' &&
          (count == null || suggested < count) &&
          page.length > 0
        ) {
          setNextOffset(suggested)
        } else {
          setNextOffset(null)
        }
      } catch {
        if (!append) {
          setEvents([])
          setError('Unable to load activity log')
        }
        setNextOffset(null)
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [projectId, taskId],
  )

  useEffect(() => {
    void loadPage(0, false)
  }, [loadPage])

  const displayRows = useMemo(
    () => buildActivityDisplayRows(events),
    [events],
  )

  const csvBlobUrl = useMemo(() => {
    if (!displayRows.length) {
      return null
    }
    const headers = [
      'registered_on',
      'user',
      'point',
      'floor',
      'event',
      'duration',
      'item_id',
      'tracker',
      'job_type',
      'observed_quantity',
      'ai_change',
      'ai_confidence',
      'ai_evidence',
      'metadata',
    ]
    const lines = [
      headers.join(','),
      ...displayRows.map(row => {
        if (row.kind === 'pause') {
          return ['', '', '', '', 'Paused', row.duration, '', '', '', '', '', '', '', '']
            .map(csvEscape)
            .join(',')
        }
        const event = row.event
        return [
          event.registered_on,
          event.user_label,
          event.point_sub_id,
          event.floor_code,
          event.event_type,
          row.duration,
          event.project_item_id,
          event.tracker_name,
          event.job_type_name,
          event.observed_quantity,
          event.ai_suggestion_change,
          event.ai_suggestion_confidence,
          event.ai_suggestion_evidence,
          formatMetadata(event.metadata),
        ]
          .map(csvEscape)
          .join(',')
      }),
    ]
    const blob = new Blob([lines.join('\n')], {
      type: 'text/csv;charset=utf-8',
    })
    return URL.createObjectURL(blob)
  }, [displayRows])

  useEffect(() => {
    return () => {
      if (csvBlobUrl) {
        URL.revokeObjectURL(csvBlobUrl)
      }
    }
  }, [csvBlobUrl])

  return (
    <ConfirmOverlay
      data-review-activity-log
      onClick={event => {
        if (event.target === event.currentTarget) {
          onClose()
        }
      }}
    >
      <LogModalCard>
        <LogModalHeader>
          <div>
            <strong>Activity log</strong>
            <div style={{ color: '#686868', fontSize: 13, marginTop: 2 }}>
              Task #{taskId}
              {totalCount != null ? ` · ${totalCount} events` : ''}
              {reviewActiveSeconds != null
                ? ` · Total review time ${formatDurationHoursMinutes(
                    reviewActiveSeconds,
                  )}`
                : ''}
            </div>
          </div>
          <LogModalActions>
            {displayRows.length > 0 ? (
              <ActionButton
                type="button"
                onClick={() => {
                  if (!csvBlobUrl) {
                    return
                  }
                  const link = document.createElement('a')
                  link.href = csvBlobUrl
                  link.download = `review-task-${taskId}-activity-log.csv`
                  link.click()
                }}
              >
                Export CSV
              </ActionButton>
            ) : null}
            <CancelButton type="button" onClick={onClose}>
              Close
            </CancelButton>
          </LogModalActions>
        </LogModalHeader>

        {loading ? (
          <LoadingIndicator />
        ) : error ? (
          <EmptyState>{error}</EmptyState>
        ) : events.length === 0 ? (
          <EmptyState>No activity logged for this task yet.</EmptyState>
        ) : (
          <>
            <LogTableScroll>
              <LogTable>
                <thead>
                  <tr>
                    <LogTh>Time</LogTh>
                    <LogTh>User</LogTh>
                    <LogTh>Point</LogTh>
                    <LogTh>Event</LogTh>
                    <LogTh>Duration</LogTh>
                    <LogTh>Item</LogTh>
                    <LogTh>Tracker</LogTh>
                    <LogTh>Job type</LogTh>
                    <LogTh>Qty</LogTh>
                    <LogTh>AI</LogTh>
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map(row => {
                    if (row.kind === 'pause') {
                      return (
                        <tr key={row.key}>
                          <LogPauseTd />
                          <LogPauseTd />
                          <LogPauseTd />
                          <LogPauseTd>Paused</LogPauseTd>
                          <LogPauseTd>{row.duration}</LogPauseTd>
                          <LogPauseTd />
                          <LogPauseTd />
                          <LogPauseTd />
                          <LogPauseTd />
                          <LogPauseTd />
                        </tr>
                      )
                    }
                    const event = row.event
                    return (
                    <tr key={event.id}>
                      <LogTd>
                        {event.registered_on
                          ? new Date(event.registered_on).toLocaleString()
                          : '—'}
                      </LogTd>
                      <LogTd>{event.user_label || '—'}</LogTd>
                      <LogTd>
                        {event.point_sub_id != null
                          ? String(event.point_sub_id)
                          : '—'}
                        {event.floor_code ? (
                          <span style={{ color: '#686868' }}>
                            {' '}
                            ({event.floor_code})
                          </span>
                        ) : null}
                      </LogTd>
                      <LogTd>
                        {formatEventLabel(event.event_type, event.metadata)}
                        {event.event_type !== 'point_navigated' &&
                        formatMetadata(event.metadata)
                          ? ` · ${formatMetadata(event.metadata)}`
                          : ''}
                      </LogTd>
                      <LogTd>{row.duration || '—'}</LogTd>
                      <LogTd>
                        {event.project_item_id != null
                          ? String(event.project_item_id)
                          : '—'}
                      </LogTd>
                      <LogTd>{event.tracker_name || '—'}</LogTd>
                      <LogTd>{event.job_type_name || '—'}</LogTd>
                      <LogTd>
                        {event.observed_quantity == null
                          ? '—'
                          : String(event.observed_quantity)}
                      </LogTd>
                      <LogTd>
                        {event.ai_suggestion_change
                          ? `${event.ai_suggestion_change}${
                              event.ai_suggestion_confidence
                                ? ` (${event.ai_suggestion_confidence})`
                                : ''
                            }`
                          : '—'}
                      </LogTd>
                    </tr>
                    )
                  })}
                </tbody>
              </LogTable>
            </LogTableScroll>
            {nextOffset != null ? (
              <div style={{ marginTop: 12, textAlign: 'center' }}>
                <ActionButton
                  type="button"
                  disabled={loadingMore}
                  onClick={() => void loadPage(nextOffset, true)}
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </ActionButton>
              </div>
            ) : null}
          </>
        )}
      </LogModalCard>
    </ConfirmOverlay>
  )
}
